import type { Exchange, Operation, OperationResult } from 'urql';
import { filter, map, merge, pipe, share } from 'wonka';

const LOCAL_FIELD_PREFIX = '_local_';

const getMutationFieldNames = (operation: Operation): string[] => {
  const definition = operation.query.definitions[0];
  if (definition?.kind !== 'OperationDefinition') {
    return [];
  }

  return definition.selectionSet.selections.flatMap((selection) =>
    selection.kind === 'Field' ? [selection.name.value] : [],
  );
};

const hasLocalFields = (operation: Operation) => {
  if (operation.kind !== 'mutation') {
    return false;
  }

  const fieldNames = getMutationFieldNames(operation);
  return fieldNames.some((name) => name.startsWith(LOCAL_FIELD_PREFIX));
};

const localMutationExchange: Exchange =
  ({ forward }) =>
  (ops$) => {
    const shared$ = pipe(ops$, share);

    const localResults$ = pipe(
      shared$,
      filter(hasLocalFields),
      map((operation): OperationResult => {
        const fieldNames = getMutationFieldNames(operation);
        const data: Record<string, null> = {};

        for (const name of fieldNames) {
          data[name] = null;
        }

        return {
          operation,
          data,
          hasNext: false,
          stale: false,
        };
      }),
    );

    const networkResults$ = pipe(
      shared$,
      filter((operation) => !hasLocalFields(operation)),
      forward,
    );

    return merge([localResults$, networkResults$]);
  };

export default localMutationExchange;
